import { OVERVIEW_PICTURE_MOST } from "daedalus-core";
import { DEFAULT_MINIMAP_STEP, MINIMAP_BIG_SCALES, VISION_MARGIN_TILES, type Size } from "odyssey-shared";

/**
 * The Map's levels (ADR-100's amendment of 2026-10-14, decisions 39 to 42 and 45), pure: the ladder from near to far,
 * the two zooms and where each lands, the level a Map opens on, which picture and which canvas a level owes, and the
 * largest field the `nearby` crop can cover. It holds no React, no Phaser and no DOM; the window reads a `LevelState`
 * and replaces it with what these functions return.
 */

/**
 * A level of the Map, from near to far.
 */
export type MapLevel = "nearby" | "region" | "world";

/**
 * One rung of the ladder: its level and whether it is built. World is a slot until P2.12-T9.
 */
export interface LevelRung {
  readonly level: MapLevel;
  readonly built: boolean;
}

/**
 * The ladder, nearest first. A zoom out passes from one rung to the next built one.
 */
export const LEVEL_LADDER: readonly LevelRung[] = Object.freeze([
  Object.freeze({ level: "nearby" as const, built: true }),
  Object.freeze({ level: "region" as const, built: true }),
  Object.freeze({ level: "world" as const, built: false }),
]);

/**
 * Where the Map stands within one opening: the level, the Nearby step (the minimap's zoom step, which Region keeps so a
 * zoom in returns to it), whether the view follows the player, and whether Region has ever been shown, which is when its
 * canvas is first owed.
 */
export interface LevelState {
  readonly level: MapLevel;
  readonly step: number;
  readonly follow: boolean;
  readonly regionShown: boolean;
}

/**
 * What a level needs held and drawn.
 */
export interface LevelNeeds {
  readonly nearbySlot: boolean;
  readonly regionCanvas: boolean;
  readonly liveMarks: boolean;
  readonly fog: boolean;
}

/**
 * The largest Nearby step: the closest zoom.
 */
const LAST_STEP = MINIMAP_BIG_SCALES.length - 1;

/**
 * Brings a stored Nearby step into the ladder.
 *
 * @param step A step, possibly stale or foreign.
 * @returns A whole step from 0 to the last, or the default step when it is not a number.
 */
function clampStep(step: number): number {
  if (!Number.isFinite(step)) {
    return DEFAULT_MINIMAP_STEP;
  }
  return Math.max(0, Math.min(LAST_STEP, Math.floor(step)));
}

/**
 * The state of a Map that has just been opened: Nearby, at the kept step, centred on the player.
 *
 * @param step The Nearby step kept on this device.
 * @returns The state, with no Region canvas owed yet.
 */
export function openLevel(step: number): LevelState {
  return { level: "nearby", step: clampStep(step), follow: true, regionShown: false };
}

/**
 * The state when the Map is opened again within a session: always Nearby on the player, the step kept, and the fact
 * that Region has been shown kept, since its canvas lives for the session (decision 42).
 *
 * @param last The state the Map had when it was shut.
 * @returns The state to open on.
 */
export function resetLevel(last: LevelState): LevelState {
  return { level: "nearby", step: clampStep(last.step), follow: true, regionShown: last.regionShown };
}

/**
 * The scale of the Nearby field at a state's step.
 *
 * @param state The state.
 * @returns Logical pixels a tile.
 */
export function levelScale(state: LevelState): number {
  return MINIMAP_BIG_SCALES[clampStep(state.step)] ?? 0;
}

/**
 * Whether a zoom out has anywhere to go: every Nearby step, but not Region while World is not built.
 *
 * @param state The state.
 * @returns True when `zoomOut` changes the state.
 */
export function canZoomOut(state: LevelState): boolean {
  if (state.level === "nearby") {
    return true;
  }
  const next = LEVEL_LADDER[LEVEL_LADDER.findIndex((rung) => rung.level === state.level) + 1];
  return next !== undefined && next.built;
}

/**
 * Whether a zoom in has anywhere to go: Region and any Nearby step but the closest.
 *
 * @param state The state.
 * @returns True when `zoomIn` changes the state.
 */
export function canZoomIn(state: LevelState): boolean {
  return state.level === "region" || (state.level === "nearby" && clampStep(state.step) < LAST_STEP);
}

/**
 * One zoom out. Within Nearby it takes a step to a smaller scale; from the first step it lands at Region, following the
 * player, and marks Region as shown. It changes nothing where `canZoomOut` is false.
 *
 * @param state The state.
 * @returns The new state; the same values when there is no further out.
 */
export function zoomOut(state: LevelState): LevelState {
  if (!canZoomOut(state)) {
    return state;
  }
  const step = clampStep(state.step);
  if (step > 0) {
    return { ...state, level: "nearby", step: step - 1 };
  }
  return { level: "region", step, follow: true, regionShown: true };
}

/**
 * One zoom in. From Region it lands at Nearby on the player, whatever the view had been panned to, at the step Nearby
 * was left from; within Nearby it takes a step to a larger scale. It changes nothing where `canZoomIn` is false.
 *
 * @param state The state.
 * @returns The new state; the same values when there is no further in.
 */
export function zoomIn(state: LevelState): LevelState {
  if (!canZoomIn(state)) {
    return state;
  }
  if (state.level === "region") {
    return { ...state, level: "nearby", step: clampStep(state.step), follow: true };
  }
  return { ...state, step: clampStep(state.step) + 1 };
}

/**
 * Which picture and which marks a level needs (decision 42 as amended by P2.12-T32): the `nearby` slot only at Nearby,
 * the Region canvas from the open, at both built levels, since it is drawn ahead in idle time, the live marks only at
 * Nearby and the fog only at Region. A level that is not built needs nothing.
 *
 * @param state The state.
 * @returns What to hold.
 */
export function levelNeeds(state: LevelState): LevelNeeds {
  const nearby = state.level === "nearby";
  const region = state.level === "region";
  return { nearbySlot: nearby, regionCanvas: nearby || region, liveMarks: nearby, fog: region };
}

/**
 * The most tiles a `nearby` crop can cover about the player at a scale, along one side: the 1,024 pixel crop in whole
 * tiles, less the vision margin on each side so a step of the player does not remake it, and less one tile for the
 * part of a tile the player stands in.
 *
 * @param scale Logical pixels a tile.
 * @returns A count of tiles, 0 for a scale that is not positive.
 */
function mostTiles(scale: number): number {
  if (!(scale > 0)) {
    return 0;
  }
  return Math.max(0, Math.floor(OVERVIEW_PICTURE_MOST / scale) - 2 * VISION_MARGIN_TILES - 1);
}

/**
 * The largest field a `nearby` crop can cover about the player, a pure function of the scale (decision 45).
 *
 * @param scale Logical pixels a tile.
 * @returns A square field in logical pixels, 0 by 0 for a scale that is not positive.
 */
export function maxNearbyField(scale: number): Size {
  const side = mostTiles(scale) * (scale > 0 ? scale : 0);
  return { width: side, height: side };
}

/**
 * Whether a field is within what the crop can cover at a scale, so a resize re-makes the crop only when it is not.
 *
 * @param field The field's size in logical pixels.
 * @param scale Logical pixels a tile.
 * @returns True when both sides are positive and no larger than `maxNearbyField`.
 */
export function nearbyFieldFits(field: Size, scale: number): boolean {
  const most = maxNearbyField(scale);
  return field.width > 0 && field.height > 0 && field.width <= most.width && field.height <= most.height;
}

/**
 * What the Region picture holds, as the window reads it.
 */
export interface RegionProgress {
  readonly started: boolean;
  readonly drawn: number;
  readonly pending: number;
}

/**
 * Whether Region has a picture to show (P2.12-T32): it is started and its first chunk, the one under the player, is
 * drawn, or it is started and owes nothing (the browser gave no canvas, or the map is too long for one picture), which
 * nobody should wait for.
 *
 * @param progress The picture's progress.
 * @returns True when the view can draw it without showing the placeholder alone.
 */
export function regionReady(progress: RegionProgress): boolean {
  return progress.started && (progress.drawn > 0 || progress.pending === 0);
}

/**
 * Which pictures are ready to draw.
 */
export interface LevelReadiness {
  readonly regionReady: boolean;
  readonly nearbyReady: boolean;
}

/**
 * The level the window draws (P2.12-T32, ADR-100's note under decision 42): the one asked for when its picture is
 * ready, else the level it was showing, kept as it was, while that one is itself ready, else none. So a zoom never shows
 * an empty field: the level being left stays drawn until the next has its first picture, then the whole of one picture
 * is swapped for the whole of the other. A level that is not built is never drawn.
 *
 * @param level The level asked for.
 * @param ready Which pictures are ready.
 * @param previous The level drawn at the last render, or null when none was.
 * @returns The level to draw, or null when none can be.
 */
export function shownLevel(level: MapLevel, ready: LevelReadiness, previous: MapLevel | null): MapLevel | null {
  const ok = (candidate: MapLevel | null): candidate is MapLevel => (candidate === "nearby" && ready.nearbyReady) || (candidate === "region" && ready.regionReady);
  if (ok(level)) {
    return level;
  }
  return ok(previous) ? previous : null;
}
